/**
 * Enhanced Authentication Error Handler for Multi-Account Systems
 * Handles token expiration, refresh, and graceful degradation
 * Updated to use tokenManager for consistent token storage
 */

import { AxiosError, AxiosRequestConfig } from 'axios';
import { apiClient } from './apiClient';
import { logger } from './logger';
import { tokenManager } from '@/app/services/auth/tokenManager';

class AuthErrorHandler {
  private readonly MAX_RETRY_ATTEMPTS = 3;

  /**
   * Handle 401 errors with automatic token refresh
   * Note: Most token refresh is now handled proactively in apiClient request interceptor
   * This is a fallback for 401 responses from the server
   */
  async handle401Error(error: AxiosError, originalConfig: AxiosRequestConfig): Promise<unknown> {
    // Get current account ID from tokenManager
    const accountId = tokenManager.getAccountContext();
    
    if (!accountId) {
      // No active account — user is not logged in. Do NOT redirect; the caller
      // (e.g. PublicAppShowcase) may handle 401 gracefully for public pages.
      logger.warn('No active account ID found, skipping auth error handling');
      return Promise.reject(error);
    }

    // Prevent infinite retry loops
    if ((originalConfig._retryCount || 0) >= this.MAX_RETRY_ATTEMPTS) {
      logger.error('Max retry attempts reached, redirecting to login');
      this.redirectToLogin();
      return Promise.reject(error);
    }

    originalConfig._retryCount = (originalConfig._retryCount || 0) + 1;

    try {
      // ⚠ force: true — do NOT drop this.
      //
      // The server has just told us this token is unacceptable, so local clock validity is
      // irrelevant. Un-forced, `refreshTokensIfNeeded()` short-circuits on exactly that check
      // (`isAccessTokenValid()`, a 5-minute buffer) and returns `true` WITHOUT refreshing. The retry
      // below then replayed the identical dead token, 401'd again, and after MAX_RETRY_ATTEMPTS
      // `redirectToLogin()` cleared the session. So every 401 on a not-yet-expired token was an
      // unrecoverable logout in which no refresh was ever attempted — the opposite of this handler's
      // purpose, and invisible because the logs said "Token refreshed successfully".
      logger.info('Attempting forced token refresh via tokenManager', { accountId });
      const refreshSuccess = await tokenManager.refreshTokensIfNeeded(true);
      
      if (!refreshSuccess) {
        logger.error('Token refresh failed via tokenManager');
        this.redirectToLogin();
        return Promise.reject(error);
      }
      
      // Get the new token from tokenManager
      const tokens = tokenManager.getTokens();
      const newToken = tokens?.accessToken;
      
      if (!newToken) {
        logger.error('No token available after refresh');
        this.redirectToLogin();
        return Promise.reject(error);
      }
      
      // Update the original request with new token
      originalConfig.headers = originalConfig.headers || {};
      originalConfig.headers.Authorization = `Bearer ${newToken}`;
      
      logger.info('Token refreshed successfully via tokenManager, retrying request', { accountId });
      
      // Retry the original request
      return apiClient.request(originalConfig);
      
    } catch (refreshError) {
      logger.error('Token refresh failed', refreshError, { accountId });
      
      // Clear tokens and redirect to login
      tokenManager.clearTokens();
      this.redirectToLogin();
      
      return Promise.reject(refreshError);
    }
  }

  /**
   * Redirect to login as last resort
   */
  private redirectToLogin(): void {
    logger.info('Redirecting to login due to authentication failure');
    
    // Clear all tokens using tokenManager
    tokenManager.clearTokens();
    
    // Redirect to login
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
}

// Extend AxiosRequestConfig to include retry count
declare module 'axios' {
  interface AxiosRequestConfig {
    _retryCount?: number;
  }
}

export const authErrorHandler = new AuthErrorHandler();
